import { MuteText } from '@elements/Common';
import { CommonUtility } from '@utility/common';
import { units, unitsTextMap } from '@utility/constants/common';
import { conversionMap, ConversionUtility } from '@utility/conversions';
import { useMemo } from 'react';

interface IConversionViewerProps {
  brackets?: boolean;
  fullText?: boolean;
  hideOnNoConversion?: boolean;
  imperial?: boolean;
  originalUnit?: string;
  showUnit?: boolean;
  value: number | null | undefined;
}

export function ConversionViewer({
  brackets = false,
  fullText = false,
  hideOnNoConversion = false,
  imperial,
  originalUnit,
  showUnit = true,
  value,
}: IConversionViewerProps) {
  const conversionFunctionsMap = {
    [units.liters]: ConversionUtility.litersToGallons,
    [units.milliLiters]: ConversionUtility.millitersToFluidOunces,
    [units.kiloGrams]: ConversionUtility.kiloToPound,
    [units.grams]: ConversionUtility.gramsToOunces,
    [units.centimeters]: ConversionUtility.centiToInch,
    [units.gallons]: ConversionUtility.gallonsToLiters,
    [units.fluidOunces]: ConversionUtility.fluidOuncesToMilliters,
    [units.pounds]: ConversionUtility.poundToKilo,
    [units.ounces]: ConversionUtility.ouncesToGrams,
    [units.inches]: ConversionUtility.inchToCenti,
  };

  const needsConversion = useMemo(() => {
    if (originalUnit === units.units || !originalUnit) return false;

    if (imperial) {
      if (
        ![
          units.fluidOunces,
          units.ounces,
          units.pounds,
          units.gallons,
          units.inches,
          // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type '"gal" | "fl oz...
        ].includes(originalUnit)
      ) {
        return true;
      }
    } else if (
      ![
        units.milliLiters,
        units.grams,
        units.kiloGrams,
        units.liters,
        units.centimeters,
        // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type '"L" | "mL" | "...
      ].includes(originalUnit)
    ) {
      return true;
    }
    return false;
  }, [imperial, originalUnit]);

  const convertedValue = useMemo(() => {
    let toReturn = value;
    if (
      needsConversion &&
      originalUnit &&
      value !== null &&
      value !== undefined
    ) {
      const calculated = conversionFunctionsMap[originalUnit]?.(value);
      if (calculated !== undefined) {
        toReturn = calculated;
      }
    }
    // @ts-expect-error - TS2322: Type 'string | undefined' is not assignable to type 'number | null | undefined'.
    toReturn = CommonUtility.numberWithCommas(
      CommonUtility.roundNumber(toReturn),
    );
    return toReturn;
  }, [needsConversion, value]);

  const convertedUnit = useMemo(() => {
    let calUnit = originalUnit;
    if (needsConversion && originalUnit) {
      calUnit = conversionMap[originalUnit];
    }

    if (fullText) {
      // @ts-expect-error - TS2538: Type 'undefined' cannot be used as an index type.
      calUnit = unitsTextMap[calUnit] || calUnit;
    }

    return calUnit;
  }, [needsConversion, originalUnit]);

  if (!value) return '';

  if (!originalUnit) return value;

  if (!needsConversion) {
    if (hideOnNoConversion) {
      return '';
    }

    return (
      <>
        {!!brackets && '('}
        {convertedValue}{' '}
        {showUnit && <MuteText>{convertedUnit || 'pieces'}</MuteText>}
        {!!brackets && ')'}
      </>
    );
  }

  return (
    <>
      {!!brackets && '('}
      {convertedValue}{' '}
      {showUnit && <MuteText>{convertedUnit || 'pieces'}</MuteText>}
      {!!brackets && ')'}
    </>
  );
}
